/* eslint-disable react/jsx-props-no-spreading */
import { useCallback, useState, useMemo } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import { Box, Typography, Button, Stack, alpha } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { UploadedFile } from "./types";
import { useNotification } from "../../context/notification-provider";
import ACCEPTED_FILE_TYPES from "../../constants/import";

interface FileDropzoneProps {
  files: UploadedFile[];
  onFilesAdded: (newFiles: File[]) => void;
  disabled?: boolean;
}

const FileDropzone = ({
  files,
  onFilesAdded,
  disabled = false,
}: FileDropzoneProps) => {
  const { showNotification } = useNotification();
  const [error, setError] = useState("");

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        setError(`Only ${ACCEPTED_FILE_TYPES.join(", ")} files are allowed`);
        return;
      }

      setError("");

      const duplicateFiles: File[] = [];
      const filesToReplace: File[] = [];
      const completelyNewFiles: File[] = [];

      acceptedFiles.forEach((newFile) => {
        const existing = files.find((f) => f.file.name === newFile.name);

        if (!existing) {
          completelyNewFiles.push(newFile);
          return;
        }

        const isDuplicate =
          existing.file.size === newFile.size &&
          existing.file.lastModified === newFile.lastModified;

        if (isDuplicate) {
          duplicateFiles.push(newFile);
          return;
        }

        filesToReplace.push(newFile);
      });

      if (duplicateFiles.length > 0) {
        showNotification({
          message: `These files are duplicates: ${duplicateFiles
            .map((f) => f.name)
            .join(", ")}`,
          severity: "warning",
          duration: 5000,
        });
      }

      if (filesToReplace.length > 0) {
        showNotification({
          message: `Replaced: ${filesToReplace.map((f) => f.name).join(", ")}`,
          severity: "info",
          duration: 5000,
        });
      }

      const updatedFiles: File[] = [];

      const keptExistingFiles = files
        .filter(
          (stored) => !filesToReplace.some((f) => f.name === stored.file.name),
        )
        .map((f) => f.file);

      updatedFiles.push(...keptExistingFiles);

      updatedFiles.push(...filesToReplace);

      updatedFiles.push(...completelyNewFiles);

      onFilesAdded(updatedFiles);
    },
    [files, onFilesAdded, showNotification],
  );

  const dropzoneConfig = useMemo(
    () => ({
      onDrop,
      multiple: true,
      disabled,
      accept: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          ACCEPTED_FILE_TYPES,
      },
    }),
    [onDrop, disabled],
  );

  const { getRootProps, getInputProps, isDragActive } =
    useDropzone(dropzoneConfig);

  const rootProps = useMemo(() => getRootProps(), [getRootProps]);
  const inputProps = useMemo(() => getInputProps(), [getInputProps]);

  const totalSizeKB = files.reduce((acc, f) => acc + f.file.size, 0) / 1024;

  const getBorderColor = () => {
    if (disabled) return "grey.400";
    if (error) return "error.main";
    if (isDragActive) return "primary.main";
    return "grey.400";
  };

  const getDropzoneText = () => {
    if (disabled) return "Uploading disabled";
    if (error) return "Invalid file type";
    if (isDragActive)
      return `Drop the ${ACCEPTED_FILE_TYPES.join(", ")} file here`;
    return "Drag & drop your file here";
  };

  return (
    <Box sx={{ mt: 2 }}>
      {!files.length ? (
        <Box
          {...rootProps}
          sx={{
            border: "2px dashed",
            borderColor: getBorderColor(),
            borderRadius: 3,
            padding: 5,
            textAlign: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            transition: "all 0.25s ease",
            backgroundColor: isDragActive ? "action.hover" : "background.paper",
            "&:hover": disabled
              ? {}
              : {
                  borderColor: error ? "error.main" : "primary.main",
                  backgroundColor: "action.hover",
                },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <input {...inputProps} disabled={disabled} />
          <CloudUploadIcon
            sx={{
              fontSize: 48,
              color: error ? "error.main" : "primary.main",
              mb: 1,
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {getDropzoneText()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {!disabled && "or click to browse"}
          </Typography>

          {!disabled && (
            <Typography variant="caption" color="text.secondary">
              Only <strong>{ACCEPTED_FILE_TYPES.join(", ")}</strong> files are
              supported.
            </Typography>
          )}

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          {...rootProps}
          sx={{
            border: "2px dashed",
            borderColor: "success.light",
            borderRadius: 3,
            backgroundColor: "success.lighter",
            p: 5,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            "&:hover": disabled
              ? {}
              : {
                  borderColor: "success.main",
                  backgroundColor: (theme) =>
                    alpha(theme.palette.success.main, 0.1),
                },
          }}
        >
          <input {...inputProps} disabled={disabled} />

          <Stack direction="row" spacing={1} alignItems="center">
            <InsertDriveFileIcon fontSize="large" color="primary" />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {files.length} file{files.length > 1 ? "s" : ""} selected
              </Typography>
              <Typography variant="caption" color="success.dark">
                {totalSizeKB.toFixed(1)} KB total
              </Typography>
            </Box>
          </Stack>

          {!disabled && (
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 1, backgroundColor: "white" }}
            >
              Add More Files
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FileDropzone;
